import {
    useAddToCartOffCanvas,
    useAppDispatch,
    useCartLoading,
    useCartStatus,
    usePromotionCode
} from "../../../../redux/stateHooks";
import {CartStatusCodeModel} from "../../../../models/domain/cartStatusCodeModel";
import {CartServiceResponseType} from "../../../../definitions/enums";
import {Alert, Button, Col, Container, Offcanvas, Row, Spinner} from "react-bootstrap";
import {PromotionModel} from "../../../../models/domain/promotionModel";
import {PromotionEvaluationModel} from "../../../../models/domain/promotionEvaluationModel";
import {FaCheck} from "react-icons/fa";
import {AddToCartWarranties} from "./warranties";
import {AddToCartDesignServices} from "./services";
import {currencyFormatter} from "../../../../utils/helpers";
import {SkeletonAddToCartOffCanvas} from "../../../../components/skeletonAddToCartOffCanvas";
import {AddToCartItemDetails} from "./addToCartItemDetails";
import {Recommendations} from "./recommendations";
import {BiErrorCircle} from "react-icons/bi";
import {BsCheckLg} from "react-icons/bs";
import {AiOutlineClose} from "react-icons/ai";
import React, {Suspense} from "react";
import {SkeletonCard} from "../../../../components/skeletonCard";
import {addToCartOffCanvasActions} from "../../../../redux/slices/carts";

export const AddToCartOffCanvas = () => {
    const dispatch = useAppDispatch()
    const {itemCount, subtotal} = useCartStatus();

    // Format items to a string like (5 Items) or (1 Item)
    const formattedItems = itemCount === 1 ? '1 Item' : `${itemCount} Items`;
    const {show} = useAddToCartOffCanvas();
    const loading = useCartLoading();

    function renderContent() {
        if (loading) return <SkeletonAddToCartOffCanvas/>

        return <>
            <Container className="pt-3 px-3">
                <Row className="border-bottom">
                    <Col>
                        <AddToCartItemDetails/>
                        <AddToCartMessages/>
                        <AddToCartPromotionMessage/>
                        <AddToCartServices/>
                    </Col>
                </Row>
                <Recommendations/>
            </Container>
            <Container
                className="atc-offcanvas-footer sticky-bottom bg-white px-4 pt-1 pb-3 border-top border-2">
                <Row>
                    <Col className="px-0">
                        {itemCount &&
                            <>
                                <div className="text-center fs-5 py-2">
                                    Cart Total ({formattedItems}):{' '}
                                    <span className="fw-bold fs-4">{currencyFormatter.format(subtotal ?? 0)}</span>
                                    <div className="text-muted" style={{fontSize: '11px'}}>
                                        Taxes & Shipping are calculated during checkout.
                                    </div>
                                </div>
                                <Button href={"/ShoppingCart"}
                                        variant="success"
                                        className="w-100"
                                        size="lg">
                                    View Cart
                                </Button>
                            </>
                        }
                        <Button
                            size="lg"
                            variant="link"
                            aria-label="Close and Continue Shopping"
                            className="w-100 mb-2 mt-1"
                            onClick={() => dispatch(addToCartOffCanvasActions.setShow(false))}
                        >Continue Shopping</Button>
                    </Col>
                </Row>
            </Container>
        </>;
    }

    return (
        <Offcanvas id="atc-offcanvas" placement="end" show={show}
                   onHide={() => dispatch(addToCartOffCanvasActions.setShow(false))}>
            <Offcanvas.Header
                className="d-flex justify-content-between align-items-center border-bottom border-2 pb-1 px-3">
                <Offcanvas.Title className="fs-3 fw-normal">
                    <OffCanvasTitle/>
                </Offcanvas.Title>
                <Button variant="link" aria-label="Close and Continue Shopping"
                        onClick={() => dispatch(addToCartOffCanvasActions.setShow(false))}>
                    <span className="">Continue Shopping</span>
                    <AiOutlineClose className="ms-1" size={20} aria-hidden/>
                </Button>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0">
                <Suspense fallback={<SkeletonCard/>}>
                    {renderContent()}
                </Suspense>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

const OffCanvasTitle = () => {
    const loading = useCartLoading();
    const {errorType} = useCartStatus()

    if (loading) return <OffCanvasLoadingTitle/>;
    if (errorType === CartServiceResponseType.TransientError || errorType === CartServiceResponseType.FailedToCreateCartItem) return <OffCanvasErrorTitle/>;
    if (errorType === CartServiceResponseType.Success) return <OffCanvasSuccessTitle/>;

    // Default return if none of the above conditions are met
    return null;
};

const AddToCartMessages = () => {
    const {cartStatusCode, errorType} = useCartStatus();
    const model = cartStatusCode ? new CartStatusCodeModel(cartStatusCode) : undefined;

    switch (errorType) {
        case CartServiceResponseType.Success:
            return (model?.message ? // success
                <div className="pb-3">
                    <Alert variant="warning">
                        {model.message}
                    </Alert>
                </div>
                : <></>)
        case CartServiceResponseType.FailedToCreateCartItem:
            return (model?.message ?
                <div className="pb-3">
                    <Alert variant="danger">
                        {model.message}
                    </Alert>
                </div> : <></>)
        case CartServiceResponseType.TransientError:
            return (
                <div className="pb-3">
                    <Alert variant="danger">
                        We're sorry, but there appears to be a temporary issue adding this item to your cart. Please try
                        again.
                    </Alert>
                </div>
            )
    }

    return <></>;
};

const AddToCartPromotionMessage = () => {
    const promotionCode = usePromotionCode();
    const {promotion} = useAddToCartOffCanvas();
    const {promotionEvaluation} = useCartStatus();
    const promotionModel = promotion ? new PromotionModel(promotion) : undefined;
    const promotionEvaluationModel = promotionEvaluation ? new PromotionEvaluationModel(promotionCode, promotionEvaluation) : undefined;

    if (!promotionEvaluationModel || !promotionModel) return (<></>);
    if (promotionEvaluationModel.status === "Unknown") return (<></>);

    const ValidPromotion = () => {
        if (!promotionModel?.appliedDescription) return null;
        return (
            <>
                <FaCheck className="me-1 text-success"/>
                <small className="text-success fw-bold">
                    {promotionModel.appliedDescription}
                </small>
            </>
        );
    };

    const InvalidPromotion = () => (
        <>
            <small className="text-danger fw-bold">
                {promotionModel?.appliedDescription}
            </small>
            <br/>
            <small className="text-muted">
                {promotionEvaluationModel?.message}
            </small>
        </>
    );

    return (
        <div className="pb-3">
            {promotionEvaluation?.status === "Valid" ? <ValidPromotion/> : <InvalidPromotion/>}
        </div>
    );
};

const AddToCartServices = () => (
    <div>
        <AddToCartWarranties/>
        <div className="my-1">
            <AddToCartDesignServices/>
        </div>
    </div>
);

const OffCanvasLoadingTitle = React.memo(() => {
    return <>
        <div className="d-flex align-items-center">
            <Spinner animation="border"
                     role="status"
                     className="text-center opacity-75"
                     variant="secondary">
                <span className="visually-hidden">
                    Loading...
                </span>
            </Spinner>
            <div className="text-secondary fs-4 ms-1 d-inline">
                Adding <span className="d-none d-md-inlin">to cart</span>...
            </div>
        </div>
    </>;
})

const OffCanvasSuccessTitle = React.memo(() => (
    <>
        <BsCheckLg className="me-1 text-success mb-1" size={32}/>
        <span className="d-none d-md-inline">Item</span> Added
    </>
));

const OffCanvasErrorTitle = React.memo(() => (
    <>
        <span className="text-danger">
            <BiErrorCircle className="me-1 text-danger mb-1" size={32}/>
            Error
        </span>
    </>
));